import { useState, useEffect } from 'react'
import ProfessionalProfileModal from './ProfessionalProfileModal'
import BookingCalendar from './BookingCalendar'
import FavoriteButton from './FavoriteButton'
import { getAllProfessionals, Professional, isProfessionalBookingEligible } from '../lib/professionalService'
import { getUserReviewSummary, ReviewSummary } from '../lib/reviewService'
import { auth } from '../lib/firebase'

const SWE_SPECIALIZATIONS = [
  'All',
  'Frontend Engineer',
  'Backend Engineer',
  'Full Stack Engineer',
  'iOS Developer',
  'Android Developer',
  'Mobile Developer',
  'ML Engineer',
  'Data Engineer',
  'DevOps Engineer',
  'Security Engineer',
  'Platform Engineer',
  'Cloud Engineer',
]

// Short label for chip display
const CHIP_LABELS: Record<string, string> = {
  'All': 'All',
  'Frontend Engineer': 'Frontend',
  'Backend Engineer': 'Backend',
  'Full Stack Engineer': 'Full-stack',
  'iOS Developer': 'iOS',
  'Android Developer': 'Android',
  'Mobile Developer': 'Mobile',
  'ML Engineer': 'ML',
  'Data Engineer': 'Data',
  'DevOps Engineer': 'DevOps',
  'Security Engineer': 'Security',
  'Platform Engineer': 'Platform',
  'Cloud Engineer': 'Cloud',
}

// Short label for card subtitle
function getSpecLabel(specialization: string): string {
  return CHIP_LABELS[specialization] || specialization.replace(' Engineer', '').replace(' Developer', '')
}

// Format experience string to short form
function formatExperience(exp: string): string {
  if (!exp) return ''
  // Already short like "5 yrs" or "1+ yr"
  if (exp.includes('yr')) return exp
  // Extract number from strings like "1-2 years", "5+ years", "3 years"
  const match = exp.match(/(\d+\+?(?:-\d+)?)\s*(?:years?|yrs?)/)
  if (match) {
    const num = match[1]
    return num.includes('+') || parseInt(num) === 1 ? `${num} yr` : `${num} yrs`
  }
  return exp
}

interface FindProfessionalsProps {
  onNavigateToCalendar?: () => void
}

export default function FindProfessionals({ onNavigateToCalendar }: FindProfessionalsProps) {
  const [selectedSpecialization, setSelectedSpecialization] = useState('All')
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isBookingCalendarOpen, setIsBookingCalendarOpen] = useState(false)
  const [professionalToBook, setProfessionalToBook] = useState<Professional | null>(null)

  // Real professional data
  const [professionals, setProfessionals] = useState<Professional[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewSummaries, setReviewSummaries] = useState<Record<string, ReviewSummary>>({})

  // Fetch professionals on component mount
  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        setLoading(true)
        setError(null)

        const fetchedProfessionals = await getAllProfessionals()

        // Filter out professionals without completed Stripe Connect setup
        const eligibleProfessionals = fetchedProfessionals.filter(professional =>
          isProfessionalBookingEligible(professional)
        )

        setProfessionals(eligibleProfessionals)

        // Fetch review summaries for all professionals
        const summaries: Record<string, ReviewSummary> = {}
        await Promise.all(
          eligibleProfessionals.map(async (professional) => {
            try {
              const summary = await getUserReviewSummary(professional.uid)
              if (summary) {
                summaries[professional.uid] = summary
              }
            } catch (error) {
              console.error(`Error fetching review summary for ${professional.uid}:`, error)
            }
          })
        )
        setReviewSummaries(summaries)

      } catch (err) {
        console.error('Error fetching professionals:', err)
        setError('Failed to load professionals. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchProfessionals()
  }, [])

  const handleProfileClick = (professional: Professional) => {
    setSelectedProfessional(professional)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProfessional(null)
  }

  const handleBookClick = (professional: Professional) => {
    setProfessionalToBook(professional)
    setIsBookingCalendarOpen(true)
  }

  const handleCloseBookingCalendar = () => {
    setIsBookingCalendarOpen(false)
    setProfessionalToBook(null)
  }

  const filteredProfessionals = professionals.filter(professional => {
    // Exclude current user from results
    const isCurrentUser = auth.currentUser && professional.uid === auth.currentUser.uid
    if (isCurrentUser) return false

    if (selectedSpecialization === 'All') return true
    return professional.specialization === selectedSpecialization ||
           professional.title === selectedSpecialization
  })


  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40"></div>
        <span className="ml-3 text-white/50 text-sm">Loading engineers...</span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-20">
        <svg className="mx-auto w-10 h-10 text-white/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        <h3 className="mt-3 text-sm font-medium text-white">Error loading engineers</h3>
        <p className="mt-1 text-sm text-white/50">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-outline mt-4"
        >
          Try again
        </button>
      </div>
    )
  }

  // Count per specialization (for chip counts)
  const allCount = professionals.filter(p => {
    const isCurrentUser = auth.currentUser && p.uid === auth.currentUser.uid
    return !isCurrentUser
  }).length

  return (
    <div className="max-w-[1280px]">

      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
          Find an <span className="font-serif italic font-normal">engineer</span>
        </h1>
        <p className="text-white/50 mt-2 text-sm max-w-2xl">
          Book a 1-on-1 call with a working software engineer. Get real talk on what the job is
          actually like, sharpen your resume, prep for interviews, or pick up job hunting tips.
        </p>
      </header>

      {/* Specialization chips */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        {SWE_SPECIALIZATIONS.map(spec => {
          const isActive = selectedSpecialization === spec
          return (
            <button
              key={spec}
              onClick={() => setSelectedSpecialization(spec)}
              className={`filter-chip ${isActive ? 'filter-chip-active' : ''}`}
            >
              {CHIP_LABELS[spec] || spec}
              {spec === 'All' && (
                <span className="text-[11px] opacity-50 pl-1.5 ml-0.5" style={{ borderLeft: '1px solid currentColor' }}>
                  {allCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-[12.5px] text-white/50">
          <span className="text-white/90 font-medium">{filteredProfessionals.length}</span> engineer{filteredProfessionals.length !== 1 ? 's' : ''} available
          {selectedSpecialization !== 'All' && ` · ${CHIP_LABELS[selectedSpecialization] || selectedSpecialization}`}
        </div>
      </div>

      {/* Card grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredProfessionals.map((professional) => {
          const review = reviewSummaries[professional.uid]
          const hasReviews = review && review.totalReviews > 0
          const specLabel = getSpecLabel(professional.specialization || professional.title || '')
          const expLabel = formatExperience(professional.experience)

          return (
            <article key={professional.uid} className="eng-card">
              {/* Photo area */}
              <div className="eng-photo">
                <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.35 }}></div>

                {professional.profileImage ? (
                  <img
                    src={professional.profileImage}
                    alt={professional.displayName}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 1 }}
                  />
                ) : (
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 150" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ color: 'hsl(0 0% 65% / 0.45)' }}>
                    <circle cx="100" cy="60" r="22"/>
                    <path d="M52 138c4-22 24-34 48-34s44 12 48 34"/>
                  </svg>
                )}

                {/* Rate badge */}
                <span className="badge-rate">
                  ${professional.sessionRate} <span className="opacity-70 font-medium ml-0.5">/ session</span>
                </span>

                {/* Heart / favorite button */}
                <div className="absolute top-3.5 right-3.5 z-10">
                  <FavoriteButton
                    professionalId={professional.uid}
                    size="md"
                    className="!bg-black/40 !backdrop-blur-sm !shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] hover:!bg-black/60"
                  />
                </div>
              </div>

              {/* Card body */}
              <div className="flex-1 flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[16.5px] font-semibold tracking-tight-half truncate">{professional.displayName}</div>
                    <div className="text-white/50 text-[12.5px] mt-0.5 truncate">
                      {specLabel}{professional.company && <> · <span className="text-white/85">{professional.company}</span></>}
                    </div>
                  </div>
                  {expLabel && (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-white bg-white/[0.06]"
                      style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                      {expLabel}
                    </span>
                  )}
                </div>

                {/* Bio */}
                <p className="text-white/80 text-[13px] leading-relaxed mt-3 line-clamp-3">
                  {professional.bio}
                </p>

                {/* Meta row */}
                <div className="flex items-center gap-3 mt-4 text-[11.5px] text-white/50">
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                    30 min
                  </span>
                  <span className="opacity-40">·</span>
                  {hasReviews ? (
                    <span className="inline-flex items-center gap-1">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-white/85"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
                      {review.averageRating.toFixed(1)} · {review.totalReviews} review{review.totalReviews !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="italic">No reviews yet</span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-5">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleBookClick(professional) }}
                    className="btn btn-primary flex-1 justify-center"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                    Book a session
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleProfileClick(professional) }}
                    className="btn btn-outline !px-3.5"
                    aria-label="View profile"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </div>
              </div>
            </article>
          )
        })}
      </section>

      {/* Empty state */}
      {filteredProfessionals.length === 0 && (
        <div className="text-center py-20">
          <svg className="mx-auto w-10 h-10 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9.5" r="2.4"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M14.5 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5"/></svg>
          <h3 className="mt-3 text-sm font-medium text-white">No engineers found</h3>
          <p className="mt-1 text-sm text-white/50">
            Try a different specialization or check back later.
          </p>
          {selectedSpecialization !== 'All' && (
            <button
              onClick={() => setSelectedSpecialization('All')}
              className="btn btn-outline mt-4"
            >
              Show all engineers
            </button>
          )}
        </div>
      )}

      {/* Professional Profile Modal */}
      <ProfessionalProfileModal
        professional={selectedProfessional}
        open={isModalOpen}
        onClose={handleCloseModal}
        onBookSession={handleBookClick}
      />

      {/* Booking Calendar */}
      <BookingCalendar
        open={isBookingCalendarOpen}
        onClose={handleCloseBookingCalendar}
        professional={professionalToBook}
        onNavigateToCalendar={onNavigateToCalendar}
      />
    </div>
  )
}
